declare global {
  interface Window {
    injectedWeb3?: Record<string, any>;
  }
}

import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { web3Enable, web3Accounts } from '@polkadot/extension-dapp';
import { InjectedAccountWithMeta } from '@polkadot/extension-inject/types';

interface WalletContextType {
  accounts: InjectedAccountWithMeta[];
  selectedAccount?: InjectedAccountWithMeta;
  setSelectedAccount: React.Dispatch<InjectedAccountWithMeta | undefined>;
  onConnectWallet: () => Promise<void>;
  hasExtension: boolean | null;
  disconnectWallet: () => void;
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

interface WalletProviderProps {
  children: ReactNode;
}

const useFetchAccounts = () => {
  const [accounts, setAccounts] = useState<InjectedAccountWithMeta[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<InjectedAccountWithMeta>();
  const [hasExtension, setHasExtension] = useState<boolean | null>(null);

  const fetchAccounts = async () => {
    try {
      const extensions = await web3Enable('staking');
      if (extensions.length === 0) {
        console.warn('No wallet extension found or permission denied');
        return;
      }

      const allAccounts = await web3Accounts();
      if (allAccounts.length > 0) {
        setAccounts(allAccounts);
        const storedAccountAddress = localStorage.getItem('ConnectedAccount');
        const storedAccount = allAccounts.find(
          (account) => account.address === storedAccountAddress
        );
        setSelectedAccount(storedAccount ?? undefined);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  const checkExtension = async () => {
    try {
      const hasInjectedWeb3 =
        window.injectedWeb3 !== undefined && Object.keys(window.injectedWeb3).length > 0;
      const isMobile = /Mobi|Android/i.test(navigator.userAgent);
      if (isMobile) {
        setHasExtension(hasInjectedWeb3 || true);
      } else {
        setHasExtension(hasInjectedWeb3);
      }
    } catch (error) {
      console.error('Error checking extensions:', error);
      setHasExtension(false);
    }
  };

  const disconnectWallet = async () => {
    setAccounts([]);
    setSelectedAccount(undefined);
  };

  useEffect(() => {
    fetchAccounts();
    checkExtension();
  }, []);

  useEffect(() => {
    if (selectedAccount) {
      localStorage.setItem('ConnectedAccount', selectedAccount.address);
    }
  }, [selectedAccount]);

  return {
    accounts,
    selectedAccount,
    fetchAccounts,
    setSelectedAccount,
    hasExtension,
    setAccounts,
    disconnectWallet,
  };
};

export const WalletProvider: React.FC<WalletProviderProps> = ({ children }) => {
  const {
    accounts,
    selectedAccount,
    fetchAccounts,
    setSelectedAccount,
    hasExtension,
    disconnectWallet,
  } = useFetchAccounts();
  const value = useMemo(
    () => ({
      accounts,
      selectedAccount,
      setSelectedAccount,
      onConnectWallet: fetchAccounts,
      hasExtension,
      disconnectWallet,
    }),
    [accounts, selectedAccount, hasExtension]
  );

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
};

export const useWallet = (): WalletContextType => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};